/**
 * Incremental ZIP ↔ Blob adapters over fflate's streaming API.
 *
 * The convenience `zip`/`unzip` functions materialize the complete archive
 * and every expanded entry as contiguous Uint8Arrays. Large Unity `.gltfzip`
 * assets can exceed the browser's ArrayBuffer allocation ceiling before
 * GLTFLoader even starts. These adapters retain bounded chunks as Blob parts
 * and never create a whole-archive ArrayBuffer.
 */

import {
  AsyncUnzipInflate,
  Unzip,
  UnzipPassThrough,
  Zip,
  ZipPassThrough,
} from "fflate";

export function unzipBlobEntriesAsync(
  blob: Blob,
  signal?: AbortSignal,
): Promise<Map<string, Blob>> {
  return new Promise<Map<string, Blob>>((resolve, reject) => {
    const entries = new Map<string, Blob>();
    const activeFiles = new Set<{ terminate(): void }>();
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let inputFinished = false;
    let discovered = 0;
    let completed = 0;
    let settled = false;

    const cleanup = () => {
      signal?.removeEventListener("abort", onAbort);
    };
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      void reader?.cancel(error);
      for (const file of activeFiles) file.terminate();
      reject(error);
    };
    const maybeResolve = () => {
      if (settled || !inputFinished || completed !== discovered) return;
      settled = true;
      cleanup();
      resolve(entries);
    };
    const onAbort = () => {
      fail(abortError(signal));
    };

    const archive = new Unzip((file) => {
      discovered++;
      const chunks: Blob[] = [];
      activeFiles.add(file);
      file.ondata = (err, data, final) => {
        if (settled) return;
        if (err) {
          fail(err);
          return;
        }
        if (data.length > 0) chunks.push(new Blob([arrayBufferOf(data)]));
        if (!final) return;
        activeFiles.delete(file);
        entries.set(file.name, new Blob(chunks));
        completed++;
        maybeResolve();
      };
      try {
        file.start();
      } catch (err) {
        fail(err);
      }
    });
    archive.register(UnzipPassThrough);
    archive.register(AsyncUnzipInflate);
    signal?.addEventListener("abort", onAbort, { once: true });

    void (async () => {
      try {
        reader = blob.stream().getReader();
        let done = false;
        while (!done) {
          throwIfAborted(signal);
          const read = await reader.read();
          if (read.done) {
            archive.push(new Uint8Array(0), true);
            inputFinished = true;
            maybeResolve();
            done = true;
          } else {
            archive.push(read.value, false);
          }
        }
      } catch (err) {
        fail(err);
      }
    })();
  });
}

/** Stream level-0 entries into a Blob, matching Unity's `ZipHelper.SetLevel(0)`. */
export function zipBlobEntriesAsync(
  entries: ReadonlyArray<readonly [string, Blob]>,
  signal?: AbortSignal,
): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    const chunks: Blob[] = [];
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let settled = false;

    const cleanup = () => {
      signal?.removeEventListener("abort", onAbort);
    };
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      void reader?.cancel(error);
      zipper.terminate();
      reject(error);
    };
    const onAbort = () => {
      fail(abortError(signal));
    };

    const zipper = new Zip((err, data, final) => {
      if (settled) return;
      if (err) {
        fail(err);
        return;
      }
      if (data.length > 0) chunks.push(new Blob([arrayBufferOf(data)]));
      if (!final) return;
      settled = true;
      cleanup();
      resolve(new Blob(chunks, { type: "application/zip" }));
    });
    signal?.addEventListener("abort", onAbort, { once: true });

    void (async () => {
      try {
        for (const [path, blob] of entries) {
          throwIfAborted(signal);
          const output = new ZipPassThrough(path);
          zipper.add(output);
          reader = blob.stream().getReader();
          let done = false;
          while (!done) {
            const read = await reader.read();
            if (read.done) {
              output.push(new Uint8Array(0), true);
              done = true;
            } else {
              output.push(read.value, false);
            }
          }
          reader = null;
        }
        zipper.end();
      } catch (err) {
        fail(err);
      }
    })();
  });
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw abortError(signal);
}

function abortError(signal?: AbortSignal): Error {
  return signal?.reason instanceof Error
    ? signal.reason
    : new DOMException("Operation cancelled.", "AbortError");
}

function arrayBufferOf(bytes: Uint8Array): ArrayBuffer {
  if (bytes.buffer instanceof ArrayBuffer) {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  }
  return new Uint8Array(bytes).buffer;
}
